import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { REVOCATION_ENABLED, startHeartbeat, subscribe, broadcastRevoked } from './sessionRevocation';

const StaffAuthContext = createContext(null);

const STAFF_API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';

// Staff-only fetch — completely isolated from customer api.js
// Uses raw fetch with credentials:include so the auth_token cookie is sent.
// Never uses axios/api.js to avoid triggering the customer 401 interceptor.
async function staffFetch(path, options = {}) {
  const res = await fetch(`${STAFF_API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw { status: res.status, data };
  return data;
}

export function StaffAuthProvider({ children }) {
  const [staffUser,     setStaffUser]     = useState(null);
  const [activeRole,    setActiveRole]    = useState(null);
  const [permissions,   setPermissions]   = useState([]);
  const [sessionId,     setSessionId]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [pendingToken,  setPendingToken]  = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  // Single-use, short-lived proof that the password step of staff sign-in just succeeded.
  // POST /v1/staff/login/mfa derives the account from THIS value, not from a client-supplied
  // user_id, so a TOTP or recovery code alone can no longer complete a sign-in.
  const [mfaChallenge, setMfaChallenge] = useState(null);

  // Prevent concurrent fetchMe calls during hydration
  const fetchingRef = useRef(false);
  // Track whether we are on an admin path to avoid unnecessary hydration
  const isAdminPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  const fetchMe = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return null;
    fetchingRef.current = true;
    try {
      const data = await staffFetch('/v1/staff/me');
      setStaffUser(data);
      setActiveRole(data.active_role);
      setPermissions(data.permissions || []);
      setSessionId(data.session_id || null);
      return data;
    } catch (err) {
      // Only clear staff state on 401/403 — not on network errors
      // Network errors (err.status undefined) should not destroy a valid session
      if (err && (err.status === 401 || err.status === 403)) {
        setStaffUser(null);
        setActiveRole(null);
        setPermissions([]);
        setSessionId(null);
      }
      // On network errors, preserve existing state
      return null;
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // On mount: only hydrate staff session if on an admin path
  // This prevents unnecessary /v1/staff/me calls on customer pages
  useEffect(() => {
    if (isAdminPath) {
      fetchMe().finally(() => setLoading(false));
    } else {
      // Not on admin path — skip hydration, set loading false immediately
      setLoading(false);
    }
  }, [fetchMe, isAdminPath]);

  // --- Session revocation: staff scope (Batch 3) ---------------------------
  // Clears staff state so the existing staff-route guards return the user to
  // /admin/login. Kept separate from fetchMe so fetchMe's behaviour is unchanged.
  const clearStaff = useCallback(() => {
    setStaffUser(null);
    setActiveRole(null);
    setPermissions([]);
    setSessionId(null);
  }, []);

  // Verify the staff session against the server. Classifies ONLY explicit
  // 401/403 as 'revoked' (require_staff returns 403 once the StaffSession is
  // inactive, and 401 when the shared cookie is gone). Network errors / 5xx are
  // 'transient' and never log the staff user out.
  const checkStaffSession = useCallback(async () => {
    try {
      await staffFetch('/v1/staff/me');
      return 'ok';
    } catch (err) {
      const status = err && err.status;
      return (status === 401 || status === 403) ? 'revoked' : 'transient';
    }
  }, []);

  // Re-verify on demand (used by the broadcast listener). Single-flight so a
  // burst of cross-tab events cannot stack /v1/staff/me calls.
  const revalidatingRef = useRef(false);
  const revalidateStaff = useCallback(async () => {
    if (revalidatingRef.current) return;
    revalidatingRef.current = true;
    try {
      const result = await checkStaffSession();
      if (result === 'revoked') clearStaff();
    } finally {
      revalidatingRef.current = false;
    }
  }, [checkStaffSession, clearStaff]);

  // Track auth state for the (mount-once) broadcast listener without resubscribing.
  const isStaffAuthed = !!staffUser;
  const authedRef = useRef(false);
  useEffect(() => { authedRef.current = isStaffAuthed; }, [isStaffAuthed]);

  // Staff heartbeat: runs ONLY while a staff user is authenticated. Detects
  // cross-device / other-browser staff logout, force-logout, revoke-all,
  // suspension, etc. (all of which flip StaffSession.is_active -> require_staff
  // 403) and shared-cookie loss (401). StrictMode-safe: each startHeartbeat()
  // returns its own stop() which cleanup calls, leaving exactly one heartbeat.
  useEffect(() => {
    if (!REVOCATION_ENABLED || !isStaffAuthed) return undefined;
    const stop = startHeartbeat({
      scope: 'staff',
      check: checkStaffSession,
      onRevoked: () => { clearStaff(); },
    });
    return () => { stop(); };
  }, [isStaffAuthed, checkStaffSession, clearStaff]);

  // Staff broadcast listener: reacts to same-origin cross-tab events. Staff
  // reacts to 'session-revoked' scope 'staff' (a sibling staff logout) or 'all'
  // (a customer logout clears the shared .visinvoke.com cookie, so staff loses
  // auth too), and to 'session-revalidate' scope 'all'. A 'staff'-scoped event
  // never reaches customer tabs; customer-scoped events are ignored here. On any
  // relevant event the tab re-verifies /v1/staff/me: a kept session (200) stays,
  // a revoked one (401/403) clears staff state -> guard returns to /admin/login.
  // Self-emitted events are suppressed inside sessionRevocation.js.
  useEffect(() => {
    if (!REVOCATION_ENABLED) return undefined;
    const unsubscribe = subscribe((msg) => {
      if (!msg || !authedRef.current) return; // ignore when no staff session
      const s = msg.scope;
      const relevant =
        (msg.type === 'session-revoked'    && (s === 'staff' || s === 'all')) ||
        (msg.type === 'session-revalidate' &&  s === 'all');
      if (!relevant) return;
      revalidateStaff();
    });
    return () => { unsubscribe(); };
  }, [revalidateStaff]);

  const staffLogin = async (email, password) => {
    const data = await staffFetch('/v1/staff/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (data.status === 'mfa_setup_required') {
      setPendingToken(data.pending_token);
      setPendingUserId(data.user_id);
    } else if (data.status === 'mfa_required') {
      setPendingUserId(data.user_id);
      setMfaChallenge(data.mfa_challenge || null);
    } else if (data.status === 'authenticated') {
      await fetchMe();
    }
    return data;
  };

  const staffMFAVerify = async (totp_code) => {
    const data = await staffFetch('/v1/staff/login/mfa', {
      method: 'POST',
      body: JSON.stringify({ mfa_challenge: mfaChallenge, totp_code }),
    });
    if (data.status === 'authenticated') {
      setActiveRole(data.active_role);
      // Hydrate staff user BEFORE clearing pending state
      // so MFAVerify guard does not redirect back to login
      await fetchMe();
      setPendingToken(null);
      setPendingUserId(null);
      setMfaChallenge(null);
    }
    return data;
  };

  const getMFASetup = async () => {
    // Cookie is already set by the login response — no need to pass it manually
    return await staffFetch('/v1/staff/mfa/setup');
  };

  const verifyMFASetup = async (totp_code) => {
    const data = await staffFetch('/v1/staff/mfa/setup/verify', {
      method: 'POST',
      body: JSON.stringify({ totp_code }),
    });
    await fetchMe();
    return data;
  };

  const switchRole = async (role) => {
    const data = await staffFetch('/v1/staff/role-switch', {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
    setActiveRole(data.active_role);
    setPermissions(data.permissions || []);
    return data;
  };

  const staffLogout = async () => {
    try {
      await staffFetch('/v1/staff/logout', { method: 'POST' });
    } catch {
      // Ignore logout errors — clear state regardless
    }
    setStaffUser(null);
    setActiveRole(null);
    setPermissions([]);
    setSessionId(null);
    setPendingToken(null);
    setPendingUserId(null);
    setMfaChallenge(null);
    // Batch 3: tell sibling staff tabs to drop. Scope 'staff' only — staff logout
    // does NOT clear the shared cookie and does NOT revoke the customer session,
    // so customer / SCC tabs (which ignore 'staff') must stay logged in.
    broadcastRevoked({ reason: 'staff-logout', scope: 'staff' });
  };

  const hasPermission = (perm) => permissions.includes(perm);

  return (
    <StaffAuthContext.Provider value={{
      staffUser, activeRole, permissions, sessionId, loading,
      pendingToken, pendingUserId, mfaChallenge,
      staffLogin, staffMFAVerify, getMFASetup, verifyMFASetup,
      switchRole, staffLogout, hasPermission, fetchMe,
    }}>
      {children}
    </StaffAuthContext.Provider>
  );
}

export const useStaffAuth = () => useContext(StaffAuthContext);

export const ROLE_META = {
  OWNER:               { color: '#C9A84C', bg: 'rgba(201,168,76,0.12)',  label: 'Owner',               risk: 'Critical' },
  CO_OWNER:            { color: '#A855F7', bg: 'rgba(168,85,247,0.12)',  label: 'Co-Owner',             risk: 'Critical' },
  OPERATIONS_ADMIN:    { color: '#F97316', bg: 'rgba(249,115,22,0.12)',  label: 'Operations Admin',     risk: 'High'     },
  SUPPORT:             { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  label: 'Support',              risk: 'Low'      },
  MODERATION:          { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  label: 'Moderation',           risk: 'Medium'   },
  CONTENT_MANAGER:     { color: '#22C55E', bg: 'rgba(34,197,94,0.12)',   label: 'Content Manager',      risk: 'Medium'   },
  GENERATOR_QA:        { color: '#84CC16', bg: 'rgba(132,204,22,0.12)',  label: 'Generator QA',         risk: 'Low'      },
  FINANCE:             { color: '#10B981', bg: 'rgba(16,185,129,0.12)',  label: 'Finance',              risk: 'High'     },
  ANALYTICS_VIEWER:    { color: '#06B6D4', bg: 'rgba(6,182,212,0.12)',   label: 'Analytics Viewer',     risk: 'Low'      },
  COMMUNITY_MANAGER:   { color: '#EC4899', bg: 'rgba(236,72,153,0.12)',  label: 'Community Manager',    risk: 'Medium'   },
  DELIVERABILITY_ADMIN:{ color: '#14B8A6', bg: 'rgba(20,184,166,0.12)',  label: 'Deliverability Admin', risk: 'Medium'   },
  TECH_OPERATOR:       { color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)',  label: 'Tech Operator',        risk: 'High'     },
  SECURITY_AUDITOR:    { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   label: 'Security Auditor',     risk: 'High'     },
  DEVELOPER_LIMITED:   { color: '#6366F1', bg: 'rgba(99,102,241,0.12)',  label: 'Developer (Limited)',  risk: 'Medium'   },
  READ_ONLY_ADMIN:     { color: '#6B7280', bg: 'rgba(107,114,128,0.12)', label: 'Read-Only Admin',      risk: 'Low'      },
};
